import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useAuth, hasModuleAccess } from "../lib/auth";
import ExportButtons from "../components/ExportButtons";
import { uomStep, validateUomQty } from "../lib/uom";

export default function AssetStore({ embedded = false }) {
  const { user } = useAuth();
  const canStoreManage = hasModuleAccess(user, "asset_management", "manage");
  const canStockEdit = canStoreManage;
  const [reg, setReg] = useState(null);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [adj, setAdj] = useState({ item_id: "", qty: 1, direction: "in", location: "A_STORE", room_id: "", reason: "", classification: "correction" });
  const localToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
  const previousDay = date => {
    const value = new Date(`${date}T12:00:00Z`);
    value.setUTCDate(value.getUTCDate() - 1);
    return value.toISOString().slice(0, 10);
  };
  const [issue, setIssue] = useState({ room_id: "", item_id: "", qty: 1, effective_date: localToday() });
  const [pool, setPool] = useState({ item_id: "", qty: 1, effective_date: localToday() });
  const [flt, setFlt] = useState({ mode: "all", q: "", lowOnly: false });
  const [customUoms, setCustomUoms] = useState([]);
  const [physicalCount, setPhysicalCount] = useState({ item_id: "", physical_qty: "", reason: "" });
  const [openingCount, setOpeningCount] = useState({ item_id: "", opening_qty: "", reason: "" });
  const [entryBusy, setEntryBusy] = useState(false);

  const load = async () => {
    const [r, i, rm, u] = await Promise.all([api.get("/assets/reports/register"), api.get("/assets/items", { params: { active: true } }), api.get("/rooms"), api.get("/assets/uoms")]);
    setReg(r.data); setItems(i.data); setRooms(rm.data); setCustomUoms(u.data?.custom || []);
  };
  useEffect(() => { load(); }, []);

  const itemUom = (id) => items.find(i => i.id === id)?.base_uom || "";

  const submitAdj = async () => {
    if (!adj.item_id || !adj.reason) return toast.error("Item and reason are required");
    const err = validateUomQty(itemUom(adj.item_id), adj.qty, customUoms);
    if (err) return toast.error(err);
    try {
      await api.post("/assets/adjustments", {
        item_id: adj.item_id, qty: Number(adj.qty), direction: adj.direction,
        location: adj.location, reason: adj.reason,
        room_id: adj.location === "A_ROOM" ? adj.room_id : null,
        classification: adj.direction === "out" ? adj.classification : null,
      });
      toast.success("Adjustment recorded (audited)"); setAdj({ item_id: "", qty: 1, direction: "in", location: "A_STORE", room_id: "", reason: "", classification: "correction" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitIssue = async () => {
    if (entryBusy) return;
    if (!issue.room_id || !issue.item_id) return toast.error("Room and item required");
    const err = validateUomQty(itemUom(issue.item_id), issue.qty, customUoms);
    if (err) return toast.error(err);
    try {
      setEntryBusy(true);
      await api.post("/assets/store-issue", {
        room_id: issue.room_id,
        lines: [{ item_id: issue.item_id, qty: Number(issue.qty) }],
        effective_date: issue.effective_date,
      });
      toast.success("Issued to room");
      setIssue({ room_id: "", item_id: "", qty: 1, effective_date: localToday() });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEntryBusy(false); }
  };

  const physicalRow = (reg?.items || []).find(r => r.item_id === physicalCount.item_id);
  const openingItem = items.find(i => i.id === openingCount.item_id);
  const physicalDifference = physicalRow && physicalCount.physical_qty !== ""
    ? Number(physicalCount.physical_qty) - Number(physicalRow.store || 0)
    : null;
  const openingDifference = openingItem && openingCount.opening_qty !== ""
    ? Number(openingCount.opening_qty) - Number(openingItem.opening_qty_draft || 0)
    : null;

  const submitPhysicalCount = async () => {
    if (!physicalCount.item_id || physicalCount.physical_qty === "" || !physicalCount.reason.trim()) {
      return toast.error("Item, physical quantity, and reason are required");
    }
    try {
      const { data } = await api.post("/assets/physical-count", {
        item_id: physicalCount.item_id,
        physical_qty: Number(physicalCount.physical_qty),
        location: "A_STORE",
        reason: physicalCount.reason.trim(),
      });
      toast.success(`Physical count recorded (${data.difference >= 0 ? "+" : ""}${data.difference})`);
      setPhysicalCount({ item_id: "", physical_qty: "", reason: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitOpeningCorrection = async () => {
    if (!openingCount.item_id || openingCount.opening_qty === "" || !openingCount.reason.trim()) {
      return toast.error("Item, corrected opening quantity, and reason are required");
    }
    try {
      const { data } = await api.post("/assets/opening-corrections", {
        item_id: openingCount.item_id,
        opening_qty: Number(openingCount.opening_qty),
        location: "A_STORE",
        reason: openingCount.reason.trim(),
      });
      toast.success(`Opening correction recorded (${data.difference >= 0 ? "+" : ""}${data.difference})`);
      setOpeningCount({ item_id: "", opening_qty: "", reason: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const nonTagged = items.filter(i => i.tracking_mode !== "tagged");
  const sharedItems = nonTagged.filter(i => i.issue_scope === "shared");
  const poolBal = (id) => (reg?.items || []).find(r => r.item_id === id)?.pool ?? 0;

  const submitPool = async (endpoint, label) => {
    if (entryBusy) return;
    if (!pool.item_id) return toast.error("Choose a shared item");
    const err = validateUomQty(itemUom(pool.item_id), pool.qty, customUoms);
    if (err) return toast.error(err);
    try {
      setEntryBusy(true);
      await api.post(`/assets/${endpoint}`, {
        lines: [{ item_id: pool.item_id, qty: Number(pool.qty) }],
        effective_date: pool.effective_date,
      });
      toast.success(label);
      setPool({ item_id: "", qty: 1, effective_date: localToday() });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEntryBusy(false); }
  };

  const balRows = (reg?.items || []).filter(r => {
    if (r.tracking_mode === "tagged") return false;
    if (flt.mode !== "all" && r.tracking_mode !== flt.mode) return false;
    if (flt.q && !(`${r.name} ${r.sku}`.toLowerCase().includes(flt.q.toLowerCase()))) return false;
    if (flt.lowOnly && !(r.min_level > 0 && r.store < r.min_level)) return false;
    return true;
  });

  const filterBar = (
    <Card className="p-3 mb-4 flex flex-wrap gap-2 items-end" data-testid="asset-store-filters">
      <div><Label className="text-xs">Search</Label><Input value={flt.q} onChange={e => setFlt({ ...flt, q: e.target.value })} placeholder="Item or SKU" className="h-9 mt-1 w-48" data-testid="asset-store-search" /></div>
      <div><Label className="text-xs">Tracking Mode</Label>
        <Select value={flt.mode} onValueChange={v => setFlt({ ...flt, mode: v })}>
          <SelectTrigger className="h-9 mt-1 w-44" data-testid="asset-store-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All (Bulk + Consumable)</SelectItem>
            <SelectItem value="bulk">Bulk Reusable</SelectItem>
            <SelectItem value="consumable">Consumable</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600 h-9 px-2">
        <input type="checkbox" checked={flt.lowOnly} onChange={e => setFlt({ ...flt, lowOnly: e.target.checked })} data-testid="asset-store-low" /> Low stock only
      </label>
    </Card>
  );

  const content = (
    <>
      <div className="mb-3 flex justify-end"><ExportButtons endpoint="/assets/export/register" filenameBase="asset_store_balance" /></div>
      {reg && !reg.opening_finalized && (
        <Card className="p-3 mb-4 border-amber-300 bg-amber-50 text-sm text-amber-900" data-testid="store-setup-pending">
          Setup Pending — opening stock is draft. Balances show only posted transactions.
        </Card>
      )}
      {filterBar}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {canStoreManage && <Card className="p-4">
          <div className="section-sub mb-3 font-medium text-slate-700">Issue to Room</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div><Label>Room</Label><Select value={issue.room_id} onValueChange={v => setIssue({ ...issue, room_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="asset-issue-room"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Item</Label><Select value={issue.item_id} onValueChange={v => setIssue({ ...issue, item_id: v })}><SelectTrigger className="h-11 mt-1" data-testid="asset-issue-item"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{nonTagged.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Qty</Label><Input type="number" min="0" step={uomStep(itemUom(issue.item_id), customUoms)} value={issue.qty} onChange={e => setIssue({ ...issue, qty: e.target.value })} className="h-11 mt-1" data-testid="asset-issue-qty" /><div className="text-[10px] text-slate-400 mt-0.5">{itemUom(issue.item_id) ? `UOM: ${itemUom(issue.item_id)}` : ""}</div></div>
            <div><Label>Entry Date</Label><Input type="date" value={issue.effective_date} min={previousDay(localToday())} max={localToday()} onChange={e => setIssue({ ...issue, effective_date: e.target.value })} className="h-11 mt-1" data-testid="asset-issue-entry-date" /></div>
            <div><Button onClick={submitIssue} disabled={entryBusy} className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="asset-issue-submit">{entryBusy ? "Posting…" : "Issue"}</Button></div>
          </div>
        </Card>}
        {canStockEdit && (
          <Card className="p-4">
            <div className="section-sub mb-3 font-medium text-slate-700">Set Physical Count</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div><Label>Item</Label><Select value={physicalCount.item_id} onValueChange={item_id => setPhysicalCount({ ...physicalCount, item_id })}><SelectTrigger className="h-11 mt-1" data-testid="asset-physical-item"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{nonTagged.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>System Qty (Store)</Label><div className="h-11 mt-1 px-3 flex items-center border rounded-md bg-slate-50" data-testid="asset-physical-system-qty">{physicalRow ? physicalRow.store : "—"}</div></div>
              <div><Label>Physical Qty</Label><Input type="number" min="0" step={uomStep(itemUom(physicalCount.item_id), customUoms)} value={physicalCount.physical_qty} onChange={e => setPhysicalCount({ ...physicalCount, physical_qty: e.target.value })} className="h-11 mt-1" data-testid="asset-physical-qty" /><div className="text-[11px] text-slate-500 mt-1" data-testid="asset-physical-difference">Difference: {physicalDifference == null ? "—" : `${physicalDifference >= 0 ? "+" : ""}${physicalDifference}`}</div></div>
              <div><Label>Reason *</Label><Input value={physicalCount.reason} onChange={e => setPhysicalCount({ ...physicalCount, reason: e.target.value })} className="h-11 mt-1" data-testid="asset-physical-reason" /></div>
            </div>
            <Button onClick={submitPhysicalCount} className="mt-3 bg-slate-900 text-white hover:bg-slate-800" data-testid="asset-physical-submit">Post Adjustment</Button>
          </Card>
        )}
      </div>

      {canStockEdit && (
        <Card className="p-4 mb-4" data-testid="asset-opening-correction-card">
          <div className="section-sub mb-3 font-medium text-slate-700">Correct Opening Stock</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div><Label>Item</Label><Select value={openingCount.item_id} onValueChange={item_id => setOpeningCount({ ...openingCount, item_id })}><SelectTrigger className="h-11 mt-1" data-testid="asset-opening-item"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{nonTagged.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Existing Opening</Label><div className="h-11 mt-1 px-3 flex items-center border rounded-md bg-slate-50" data-testid="asset-opening-system-qty">{openingItem ? openingItem.opening_qty_draft || 0 : "—"}</div></div>
            <div><Label>Corrected Opening</Label><Input type="number" min="0" step={uomStep(itemUom(openingCount.item_id), customUoms)} value={openingCount.opening_qty} onChange={e => setOpeningCount({ ...openingCount, opening_qty: e.target.value })} className="h-11 mt-1" data-testid="asset-opening-qty" /><div className="text-[11px] text-slate-500 mt-1" data-testid="asset-opening-difference">Difference: {openingDifference == null ? "—" : `${openingDifference >= 0 ? "+" : ""}${openingDifference}`}</div></div>
            <div><Label>Reason *</Label><Input value={openingCount.reason} onChange={e => setOpeningCount({ ...openingCount, reason: e.target.value })} className="h-11 mt-1" data-testid="asset-opening-reason" /></div>
          </div>
          <Button onClick={submitOpeningCorrection} className="mt-3 bg-slate-900 text-white hover:bg-slate-800" data-testid="asset-opening-submit">Post Opening Correction</Button>
        </Card>
      )}

      {canStoreManage && <Card className="p-4 mb-4" data-testid="pool-card">
        <div className="section-sub mb-1 font-medium text-slate-700">Housekeeping Common Use — Shared Pool (All Rooms)</div>
        <div className="text-xs text-slate-500 mb-3">Issue shared items from Store into one common pool (no per-room multiplication), then record consumption from the pool. Only items with Issue Scope = "All Rooms / Housekeeping Common Use" appear here.</div>
        {sharedItems.length === 0 ? (
          <div className="text-sm text-slate-400">No items are configured for shared issue yet. Set an item's Issue Scope to Shared in the Item Master.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2"><Label>Shared Item</Label>
              <Select value={pool.item_id} onValueChange={v => setPool({ ...pool, item_id: v })}>
                <SelectTrigger className="h-11 mt-1" data-testid="pool-item"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{sharedItems.map(i => <SelectItem key={i.id} value={i.id}>{i.name} (pool: {poolBal(i.id)})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Qty</Label><Input type="number" min="0" step={uomStep(itemUom(pool.item_id), customUoms)} value={pool.qty} onChange={e => setPool({ ...pool, qty: e.target.value })} className="h-11 mt-1" data-testid="pool-qty" /><div className="text-[10px] text-slate-400 mt-0.5">{itemUom(pool.item_id) ? `UOM: ${itemUom(pool.item_id)}` : ""}</div></div>
            <div><Label>Entry Date</Label><Input type="date" value={pool.effective_date} min={previousDay(localToday())} max={localToday()} onChange={e => setPool({ ...pool, effective_date: e.target.value })} className="h-11 mt-1" data-testid="pool-entry-date" /></div>
            <div><Button onClick={() => submitPool("pool-issue", "Issued to shared pool")} disabled={entryBusy} className="h-11 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="pool-issue-btn">Issue to Pool</Button></div>
            <div><Button variant="outline" onClick={() => submitPool("pool-consumption", "Consumed from shared pool")} disabled={entryBusy} className="h-11 w-full" data-testid="pool-consume-btn">Consume from Pool</Button></div>
          </div>
        )}
      </Card>}

      <Card className="p-0 overflow-x-auto">
        <table className="vr-table" data-testid="store-balance-table">
          <thead><tr><th>SKU</th><th>Item</th><th>Mode</th><th>UOM</th><th className="num">Store</th><th className="num">Pool</th><th className="num">Rooms</th><th className="num">Repair</th><th className="num">Damaged</th><th className="num">Owned Total</th><th>Alert</th></tr></thead>
          <tbody>
            {balRows.map(r => (
              <tr key={r.sku}>
                <td className="font-mono text-xs">{r.sku}</td><td>{r.name}</td><td className="text-xs">{r.tracking_mode}</td>
                <td className="text-xs">{r.tracking_mode === "tagged" ? "—" : (r.base_uom || <span className="text-amber-700">UOM Setup Required</span>)}</td>
                <td className="num">{r.store}</td><td className="num">{r.pool}</td><td className="num">{r.rooms}</td><td className="num">{r.repair}</td><td className="num">{r.damaged}</td>
                <td className="num font-semibold">{r.owned_total}</td>
                <td>{r.setup_pending && r.opening_draft > 0 ? <Badge className="bg-amber-100 border text-amber-800">Setup Pending ({r.opening_draft} draft)</Badge>
                  : (r.min_level > 0 && r.store < r.min_level ? <Badge className="bg-red-100 border text-red-800">Low</Badge> : "")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );

  if (embedded) return content;
  return (
    <Layout>
      <div className="mb-4"><h1 className="section-title">Store Inventory</h1></div>
      <AssetNav />
      {content}
    </Layout>
  );
}
